import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Input, Modal, Alert, Icon, toast } from '@/components/ui';
import { en } from '@/i18n/en';
import { useAuth } from '@/state/AuthContext';
import { useTheme } from '@/state/ThemeContext';
import { api } from '@/lib/api';
import { passwordStrength } from '@/lib/format';
import { getCompany, patchCompany, uploadCompanyLogo, deleteCompanyLogo, useCompanyLogoUrl } from '@/lib/company';

const profileSchema = z.object({ name: z.string().min(2, 'Entrez votre nom'), email: z.string().email('Adresse email invalide') });
type ProfileForm = z.infer<typeof profileSchema>;

const passwordSchema = z.object({
  current: z.string().min(1, 'Requis'),
  next: z.string().min(10, 'Au moins 10 caractères'),
  confirm: z.string(),
}).refine((v) => v.next === v.confirm, { message: 'Les mots de passe ne correspondent pas', path: ['confirm'] });
type PasswordForm = z.infer<typeof passwordSchema>;

export function SettingsPage() {
  const { user, signOut } = useAuth();
  const nav = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const t = en.dashboard.settings;

  const profile = useForm<ProfileForm>({
    defaultValues: { name: user?.name ?? '', email: user?.email ?? '' },
    resolver: async (v) => {
      const r = profileSchema.safeParse(v);
      return r.success ? { values: r.data, errors: {} } : {
        values: {},
        errors: Object.fromEntries(r.error.issues.map((i) => [i.path[0], { type: 'v', message: i.message }])),
      };
    },
  });

  const pw = useForm<PasswordForm>({
    resolver: async (v) => {
      const r = passwordSchema.safeParse(v);
      return r.success ? { values: r.data, errors: {} } : {
        values: {},
        errors: Object.fromEntries(r.error.issues.map((i) => [i.path[0], { type: 'v', message: i.message }])),
      };
    },
  });
  const pwValue = pw.watch('next') ?? '';
  const strength = passwordStrength(pwValue);

  async function saveProfile(values: ProfileForm) {
    try { await api.patch('/me', values); toast('Profil mis à jour.', 'success'); }
    catch (e) { toast(e instanceof Error ? e.message : 'Échec de l\'enregistrement', 'danger'); }
  }

  async function savePassword(values: PasswordForm) {
    try {
      await api.post('/me/password', { currentPassword: values.current, newPassword: values.next });
      pw.reset();
      toast('Mot de passe modifié avec succès.', 'success');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Échec de modification du mot de passe', 'danger');
    }
  }

  const strengthScore = strength.score;
  return (
    <div className="stack-lg settings-page">
      <header className="settings-header">
        <h1>{t.title}</h1>
        <p className="muted">Gérez vos informations de facturation, modifiez vos identifiants et contrôlez votre compte.</p>
      </header>

      <CompanySection />

      <AppearanceSection />

      <Card>
        <div className="settings-card-head">
          <h3>{t.profile}</h3>
          <p className="muted">Votre nom et e-mail associés au compte — utilisés pour vous connecter et vous adresser les communications.</p>
        </div>
        <form onSubmit={profile.handleSubmit(saveProfile)} className="form-grid">
          <Input label={en.auth.nameLabel} error={profile.formState.errors.name?.message} {...profile.register('name')} />
          <Input label={en.auth.emailLabel} type="email" error={profile.formState.errors.email?.message} {...profile.register('email')} />
          <div className="full" style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="submit" variant="primary" disabled={!profile.formState.isDirty || profile.formState.isSubmitting}>
              {profile.formState.isSubmitting ? 'Enregistrement…' : en.common.save}
            </Button>
          </div>
        </form>
      </Card>

      <Card>
        <div className="settings-card-head">
          <h3>{t.password}</h3>
          <p className="muted">10 caractères minimum. La modification du mot de passe déconnecte toutes les autres sessions.</p>
        </div>
        <form onSubmit={pw.handleSubmit(savePassword)} className="form-grid">
          <Input label="Mot de passe actuel" type="password" autoComplete="current-password" error={pw.formState.errors.current?.message} {...pw.register('current')} />
          <span />
          <Input label="Nouveau mot de passe" type="password" autoComplete="new-password" error={pw.formState.errors.next?.message} {...pw.register('next')} />
          <Input label="Confirmer le nouveau mot de passe" type="password" autoComplete="new-password" error={pw.formState.errors.confirm?.message} {...pw.register('confirm')} />
          <div className="full">
            <div className="pw-meter" data-score={strengthScore} aria-hidden>
              <span /><span /><span /><span />
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Force : {strength.label}</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <Button type="submit" variant="primary" disabled={pw.formState.isSubmitting}>
                {pw.formState.isSubmitting ? 'Enregistrement…' : 'Changer le mot de passe'}
              </Button>
            </div>
          </div>
        </form>
      </Card>

      <Card>
        <div className="settings-card-head">
          <h3>Session & Déconnexion</h3>
          <p className="muted">Vous êtes actuellement connecté en tant que {user?.email}. Vous pouvez vous déconnecter de votre session à tout moment.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #f27639, #e65422)',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 16,
            }}>
              {(user?.name || user?.email || 'M').charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ fontWeight: 600, color: 'var(--neo-text)' }}>{user?.name || 'Utilisateur Ma Facture'}</div>
              <div className="muted" style={{ fontSize: 13 }}>{user?.email}</div>
            </div>
          </div>
          <Button
            variant="secondary"
            onClick={async () => {
              await signOut();
              nav('/auth', { replace: true });
            }}
            leadingIcon={<Icon name="logout" size={16} />}
            style={{ borderColor: 'var(--neo-line-strong)', color: 'var(--neo-text)' }}
          >
            Se déconnecter de Ma Facture
          </Button>
        </div>
      </Card>

      <Card className="settings-danger">
        <div className="settings-card-head">
          <h3>{t.danger}</h3>
          <p className="muted">Action irréversible et en cascade : vos factures, fichiers téléversés, brouillons et carnet de clients seront définitivement supprimés.</p>
        </div>
        <Alert tone="warning" title="Action irréversible">
          Une fois supprimé, votre compte ne peut plus être récupéré. Téléchargez au préalable les factures que vous souhaitez conserver.
        </Alert>
        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="destructive" onClick={() => setConfirmDelete(true)} leadingIcon={<Icon name="x" size={14} />}>{t.deleteAccount}</Button>
        </div>
      </Card>

      <DeleteAccountModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onDeleted={async () => { await signOut(); nav('/', { replace: true }); }}
      />
    </div>
  );
}

function CompanySection() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['company'], queryFn: getCompany });
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [logoCacheBust, setLogoCacheBust] = useState(() => String(Date.now()));
  const logoUrl = useCompanyLogoUrl(query.data?.hasLogo, logoCacheBust);
  const [companyName, setCompanyName] = useState('');
  const [vendor, setVendor] = useState({ name: '', address: '', taxId: '', email: '', phone: '' });

  useEffect(() => {
    const c = query.data;
    if (!c) return;
    setCompanyName(c.companyName ?? '');
    setVendor({
      name: c.defaultVendor?.name ?? '',
      address: c.defaultVendor?.address ?? '',
      taxId: c.defaultVendor?.taxId ?? '',
      email: c.defaultVendor?.email ?? '',
      phone: c.defaultVendor?.phone ?? '',
    });
  }, [query.data]);

  const save = useMutation({
    mutationFn: () => patchCompany({ companyName, defaultVendor: vendor }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['company'] });
      toast('Entreprise enregistrée.', 'success');
    },
    onError: (e: unknown) => toast(e instanceof Error ? e.message : 'Échec de l\'enregistrement', 'danger'),
  });

  const upload = useMutation({
    mutationFn: (file: File) => uploadCompanyLogo(file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['company'] });
      setLogoCacheBust(String(Date.now()));
      toast('Logo téléversé.', 'success');
    },
    onError: (e: unknown) => toast(e instanceof Error ? e.message : 'Échec du téléversement', 'danger'),
  });

  const removeLogo = useMutation({
    mutationFn: () => deleteCompanyLogo(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['company'] });
      toast('Logo supprimé.', 'info');
    },
  });

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) upload.mutate(f);
    e.target.value = '';
  }

  return (
    <Card>
      <h3 style={{ marginTop: 0 }}>Profil de l'entreprise</h3>
      <p className="muted" style={{ fontSize: 13, marginTop: -8 }}>
        Utilisé pour pré-remplir l'émetteur et le logo sur les factures que vous créez.
      </p>

      <div className="form-grid" style={{ marginTop: 12 }}>
        <Input label="Nom de l'entreprise" placeholder="Votre entreprise SAS" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
        <div>
          <label className="field-label">Logo</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {query.data?.hasLogo && logoUrl ? (
              <img
                src={logoUrl}
                alt="Logo de l'entreprise"
                style={{ width: 96, height: 56, objectFit: 'contain', background: 'var(--surface-3)', borderRadius: 6, padding: 6 }}
              />
            ) : (
              <div style={{ width: 96, height: 56, background: 'var(--surface-3)', borderRadius: 6, display: 'grid', placeItems: 'center', color: 'var(--ink-4)', fontSize: 11 }}>
                Aucun logo
              </div>
            )}
            <div className="row-gap">
              <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" hidden onChange={onFile} />
              <Button variant="secondary" size="sm" onClick={() => fileInput.current?.click()} disabled={upload.isPending} leadingIcon={<Icon name="upload" size={14} />}>
                {upload.isPending ? 'Téléversement…' : query.data?.hasLogo ? 'Remplacer' : 'Téléverser'}
              </Button>
              {query.data?.hasLogo && (
                <Button variant="ghost" size="sm" onClick={() => removeLogo.mutate()} disabled={removeLogo.isPending}>
                  Supprimer
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <h4 style={{ margin: '20px 0 8px', fontSize: 13, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        Coordonnées par défaut de l'émetteur
      </h4>
      <div className="form-grid">
        <Input label="Nom ou Raison sociale" value={vendor.name} onChange={(e) => setVendor({ ...vendor, name: e.target.value })} placeholder="Identique au nom de l'entreprise par défaut" />
        <Input label="E-mail" type="email" value={vendor.email} onChange={(e) => setVendor({ ...vendor, email: e.target.value })} />
        <Input label="Téléphone" value={vendor.phone} onChange={(e) => setVendor({ ...vendor, phone: e.target.value })} />
        <Input label="N° TVA / SIRET" value={vendor.taxId} onChange={(e) => setVendor({ ...vendor, taxId: e.target.value })} />
        <div style={{ gridColumn: '1 / -1' }}>
          <Input label="Adresse" value={vendor.address} onChange={(e) => setVendor({ ...vendor, address: e.target.value })} />
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? '…' : 'Enregistrer l\'entreprise'}
        </Button>
      </div>
    </Card>
  );
}

function DeleteAccountModal({ open, onClose, onDeleted }: { open: boolean; onClose: () => void; onDeleted: () => Promise<void> }) {
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);

  const isConfirmed = confirmText.trim().toLowerCase() === 'supprimer' || confirmText.trim().toLowerCase() === 'delete';

  async function run() {
    if (!isConfirmed) return;
    setBusy(true);
    try {
      await api.delete('/me', { data: { password } });
      await onDeleted();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Échec de la suppression', 'danger');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={en.dashboard.settings.deleteConfirmTitle}
      lede={en.dashboard.settings.deleteConfirmLede}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{en.common.cancel}</Button>
          <Button variant="destructive" disabled={busy || !isConfirmed || !password} onClick={run}>{busy ? '…' : 'Supprimer mon compte'}</Button>
        </>
      }
    >
      <div className="stack">
        <Input label="Votre mot de passe" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        <Input label={en.dashboard.settings.deleteTypePrompt} value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
      </div>
    </Modal>
  );
}

function AppearanceSection() {
  const { theme, setTheme } = useTheme();

  return (
    <Card>
      <div className="settings-card-head">
        <h3>Apparence</h3>
        <p className="muted">Personnalisez le thème visuel de l'interface selon vos préférences.</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginTop: 14 }}>
        <button
          type="button"
          onClick={() => setTheme('light')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '16px 20px',
            borderRadius: 16,
            border: theme === 'light' ? '2px solid var(--neo-teal)' : '1px solid var(--neo-line)',
            background: theme === 'light' ? 'rgba(237, 92, 37, 0.08)' : 'var(--neo-surface-2)',
            color: 'var(--neo-text)',
            cursor: 'pointer',
            textAlign: 'left',
            transition: 'all 0.2s ease',
          }}
        >
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#fff1e8', border: '1px solid #f0d9ca', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ed5c25' }}>
            <Icon name="sun" size={20} />
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Mode Clair</div>
            <div style={{ fontSize: 12, color: 'var(--neo-text-3)' }}>Par défaut</div>
          </div>
        </button>

        <button
          type="button"
          onClick={() => setTheme('dark')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '16px 20px',
            borderRadius: 16,
            border: theme === 'dark' ? '2px solid var(--neo-teal)' : '1px solid var(--neo-line)',
            background: theme === 'dark' ? 'rgba(242, 118, 57, 0.15)' : 'var(--neo-surface-2)',
            color: 'var(--neo-text)',
            cursor: 'pointer',
            textAlign: 'left',
            transition: 'all 0.2s ease',
          }}
        >
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#34251f', border: '1px solid #604431', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff1e8' }}>
            <Icon name="moon" size={18} />
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Mode Sombre</div>
            <div style={{ fontSize: 12, color: 'var(--neo-text-3)' }}>Élégant & reposant</div>
          </div>
        </button>
      </div>
    </Card>
  );
}
